import toNumber from 'lodash/toNumber';
import React, { type ChangeEvent, type HTMLProps, useCallback } from 'react';
import styled from 'styled-components';

import {
  BORDER_WIDTH,
  INPUT_RANGE_HEIGHT,
  INPUT_RANGE_THUMB_SIZE,
  SLIDER_RIGHT_BG_COLOR_BY_VARIANT,
} from '@wecasa/common/src/components/input-range/style';
import type { CommonInputRangeProps } from '@wecasa/common/src/components/input-range/types';
import colors from '@wecasa/common/src/utils/colors';

import { getGradient } from '../../styles/gradient';
import Text from '../base-text/variants/text';

export const ANIMATION_DURATION = 300;

const MINIMUM_LEFT_OFFSIDE = 5;

const StyledContainer = styled.div<{
  $backgroundSize?: string;
  $hasTransition: boolean;
  $variant?: InputRangeVariant;
}>`
  height: ${INPUT_RANGE_HEIGHT}px;
  position: relative;

  &::before {
    pointer-events: none;
    z-index: 1;
    position: absolute;
    content: '';
    height: ${INPUT_RANGE_HEIGHT - BORDER_WIDTH * 2}px;
    inset: 50% 0 0 ${BORDER_WIDTH}px;
    transform: translateY(-50%);
    border-radius: 35px;
    ${({ $variant }) =>
      $variant === 'default'
        ? `
      background-image: ${getGradient()};
      background-repeat: no-repeat;
      `
        : `
      background-color: ${colors.ink100};
      `}
    width: calc(${({ $backgroundSize }) => $backgroundSize} - ${BORDER_WIDTH * 2}px);
    min-width: ${INPUT_RANGE_THUMB_SIZE + 2}px;

    /* We are disabling transition for Safari because transition on width is glitchy for this component on Safari */
    transition: ${({ $hasTransition }) => ($hasTransition ? `width ease-out ${ANIMATION_DURATION}ms` : 'none')};
  }

  &::after {
    pointer-events: none;
    width: ${INPUT_RANGE_THUMB_SIZE - BORDER_WIDTH * 2}px;
    height: ${INPUT_RANGE_THUMB_SIZE - BORDER_WIDTH * 2}px;
    border-radius: 50%;
    background-color: ${colors.white100};
    z-index: 1;
    position: absolute;
    content: '';
    left: ${({ $backgroundSize }) => {
      /* Slider thumb is a bit buggy at minimum value, we need to fix a minimum left value when it comes to min value */
      if ($backgroundSize?.includes('calc(0%')) return `${MINIMUM_LEFT_OFFSIDE}px`;
      return `calc(${$backgroundSize} - ${INPUT_RANGE_THUMB_SIZE}px)`;
    }};
    top: 50%;
    transform: translateY(-50%);
    box-shadow: 0 0 2px 0 ${colors.ink100};

    /* We are disabling transition for Safari because transition on width is glitchy for this component on Safari */
    transition: ${({ $hasTransition }) => ($hasTransition ? `left ease-out ${ANIMATION_DURATION}ms` : 'none')};
  }
`;

type InputRangeVariant = 'default' | 'black';

const StyledInput = styled.input<{ $backgroundSize?: string; $variant: InputRangeVariant }>`
  position: relative;
  appearance: none;
  width: 100%;
  height: ${INPUT_RANGE_HEIGHT}px;
  background: ${({ $variant }) => SLIDER_RIGHT_BG_COLOR_BY_VARIANT[$variant]};
  border-radius: 50px;

  &[type='range']::-webkit-slider-thumb {
    z-index: 2;
    appearance: none;
    height: ${INPUT_RANGE_THUMB_SIZE}px;
    width: ${INPUT_RANGE_THUMB_SIZE}px;
    border-radius: 50%;
    background-color: transparent;
  }

  &[type='range']::-moz-range-thumb {
    z-index: 2;
    height: ${INPUT_RANGE_THUMB_SIZE}px;
    width: ${INPUT_RANGE_THUMB_SIZE}px;
    border-radius: 50%;
    background-color: transparent;
    border: 2px solid transparent;
  }
`;

type InputNumberParams = {
  value: number;
  min: number;
  max: number;
};

function adjustPosition({ min, max, value }: InputNumberParams) {
  const remainingProgress = 1 - (value - min) / (max - min);
  return remainingProgress * (INPUT_RANGE_THUMB_SIZE + BORDER_WIDTH);
}

const getBackgroundSize = ({ min, max, value }: InputNumberParams) => {
  const leftSidePercentage = ((value - min) * 100) / (max - min);
  const adjust = adjustPosition({ value, min, max });
  return `calc(${leftSidePercentage}% + ${adjust}px)`;
};

export type InputRangeProps = CommonInputRangeProps &
  Omit<HTMLProps<HTMLInputElement>, 'ref' | 'as' | 'value' | 'onChange'> & {
    variant?: InputRangeVariant;
  };

const InputRange = React.forwardRef<HTMLInputElement, InputRangeProps>(
  (
    { variant = 'default', value, min = 0, max = 100, onChange, step = 1, error, hasTransition = true, ...props },
    ref,
  ) => {
    const backgroundSize = getBackgroundSize({ min, max, value });

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(toNumber(event.target.value));
      },
      [onChange],
    );

    return (
      <>
        {/* Wrap the input in a div was necessary to display the :before and :after style in Firefox */}
        <StyledContainer
          $variant={variant}
          $backgroundSize={backgroundSize}
          $hasTransition={hasTransition}
          data-testid='inputRangeContainer'
        >
          <StyledInput
            $variant={variant}
            $backgroundSize={backgroundSize}
            ref={ref}
            type='range'
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={handleChange}
            {...props}
          />
        </StyledContainer>
        {error && <Text color={colors.error100}>{error}</Text>}
      </>
    );
  },
);

export default InputRange;
